/**
 * WikiOnboarding — 채널 위키 게시판 소개 페이지
 *
 * 채널에 위키 게시판이 없을 때 표시.
 * "시작하기" 클릭 시 위키 게시판이 자동 생성됨.
 *
 * TasksOnboarding 패턴 미러.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import {
  BookOpen, FolderTree, Link2, MessageSquareText, Sparkles, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

interface WikiOnboardingProps {
  /** 시작하기 클릭 — 위키 게시판 생성 호출 */
  onActivate: () => void;
  activating: boolean;
  /** 보류 중인 채팅 메시지가 있는지 — 있으면 안내 문구 변경 */
  hasPendingChat?: boolean;
}

const WikiOnboarding: React.FC<WikiOnboardingProps> = ({
  onActivate,
  activating,
  hasPendingChat = false,
}) => {
  const features = [
    {
      icon: FolderTree,
      title: <Trans>Pages with hierarchy</Trans>,
      desc: <Trans>Group pages as parent and child like Notion. Organize your team's knowledge naturally.</Trans>,
    },
    {
      icon: Link2,
      title: <Trans>Wiki page linking</Trans>,
      desc: <Trans>Type @ to mention members or link to other wiki pages. Knowledge connects on its own.</Trans>,
    },
    {
      icon: MessageSquareText,
      title: <Trans>Capture chat into wiki</Trans>,
      desc: <Trans>Convert a chat message into a wiki page with one click. Never lose decisions made in chat.</Trans>,
    },
    {
      icon: Sparkles,
      title: <Trans>AI auto-format</Trans>,
      desc: <Trans>AI distills the conversation into title, sections, and bullets in wiki style.</Trans>,
    },
  ];

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
      <div className="max-w-md w-full flex flex-col items-center gap-8">
        {/* 아이콘 + 타이틀 */}
        <div className="flex flex-col items-center gap-3">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-emerald-400/20 to-teal-500/20 flex items-center justify-center">
            <BookOpen className="size-8 text-primary" />
          </div>
          <h2 className="text-lg font-bold text-[color:rgb(var(--text-title))]">
            <Trans>Team Wiki</Trans>
          </h2>
          <p className="text-sm text-muted-foreground text-center leading-relaxed max-w-[320px]">
            {hasPendingChat ? (
              <Trans>
                Set up a wiki for this channel and save the selected message as the first page.
              </Trans>
            ) : (
              <Trans>
                Build a shared knowledge base your team can keep editing together as you chat.
              </Trans>
            )}
          </p>
        </div>

        {/* 기능 카드 */}
        <div className="w-full flex flex-col gap-3">
          {features.map((feature, i) => (
            <div
              key={i}
              className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50"
            >
              <div className="size-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0 mt-0.5">
                <feature.icon className="size-4 text-primary" />
              </div>
              <div className="min-w-0">
                <h4 className="text-label font-semibold text-[color:rgb(var(--text-title))] mb-0.5">
                  {feature.title}
                </h4>
                <p className="text-meta text-muted-foreground leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 시작하기 버튼 */}
        <Button
          onClick={onActivate}
          disabled={activating}
          className="gap-2 px-6"
        >
          {activating ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              <Trans>Setting up...</Trans>
            </>
          ) : (
            <>
              <BookOpen className="size-4" />
              <Trans>Get Started</Trans>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default WikiOnboarding;
